import React, { useState, useEffect } from 'react';
import { LandingPageWrapper } from 'components';
import { useAuth, usePermission, useServicesEnabled } from '../../core';
import {
    FIELD_MAPPING,
    getColumns,
    JAVASCRIPT_TRANSFORM,
    PIPELINES,
    pipelinesAndStepsStyles,
    RULE_SET,
    STEPS,
    TabItems,
    tableHeight,
    TabsItemsType,
} from './pipelines-helper';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    PipelineFilledIcon,
    StepFilledIcon,
    EmptyPipelineIcon,
    EmptyStepIcon,
} from 'assets/images';
import { PIPELINES_AND_STEPS_PATHS } from 'constants/pipelines-and-steps-paths';
import { usePipelinesList, useStepsList } from 'hooks';
import InvalidPermissionsDialog from 'components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { Box, Typography } from '@mui/material';

export const Pipelines = (): React.JSX.Element => {
    const { isServiceEnabledFor } = useServicesEnabled();
    const { getRoles } = useAuth();
    const { hasPermissions } = usePermission();
    const isDataPipelineServiceEnabled = isServiceEnabledFor('DATA_PIPELINE');
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [rowsData, setRowsData] = useState<any>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedTab, setSelectedTab] = useState<TabsItemsType>(PIPELINES);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const columns = getColumns(selectedTab);
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const option = queryParams.get('option');
    const {
        data: pipelinesData,
        isLoading: pipelinesLoading,
        refetch: refetchPipelinesList,
    } = usePipelinesList(isDataPipelineServiceEnabled);
    const {
        data: stepsData,
        isLoading: stepsLoading,
        refetch: refetchStepsList,
    } = useStepsList(isDataPipelineServiceEnabled);

    useEffect(() => {
        if (getRoles().includes('sdzAdmin')) {
            setReadOnly(false);
        } else {
            setReadOnly(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (option) {
            setSelectedTab(option as TabsItemsType);
        }
    }, [option]);

    useEffect(() => {
        setIsLoading(true);
        if (selectedTab === PIPELINES && !pipelinesLoading) {
            setRowsData(pipelinesData?.data);
            setIsLoading(false);
        }
        if (selectedTab === STEPS && !stepsLoading) {
            refetchStepsList();
            setRowsData(stepsData);
            setIsLoading(false);
        }
    }, [selectedTab, pipelinesLoading, stepsLoading]);

    const onTabChange = (event: React.SyntheticEvent, newValue: any): void => {
        if (event) {
            setSelectedTab(newValue);
            setRowsData([]);
            setIsLoading(true);
            if (newValue === PIPELINES) {
                refetchPipelinesList();
            } else if (newValue === STEPS) {
                refetchStepsList();
            }
        }
    };

    const handleNewClick = (action: 'pipeline' | 'step'): void => {
        const permission = hasPermissions('createMetadata');
        if (permission) {
            if (action === 'pipeline') {
                navigate(PIPELINES_AND_STEPS_PATHS.CREATE_NEW_PIPELINE);
            } else if (action === 'step') {
                navigate(PIPELINES_AND_STEPS_PATHS.CREATE_NEW_STEP);
            }
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handleMenuButtonClick = (): void => {
        if (readOnly) {
            setDisplayPermissionDialog(true);
        }
    };

    const dropdownMenuList = [
        {
            label: 'New Pipeline',
            icon: PipelineFilledIcon,
            onClick: (): void => handleNewClick('pipeline'),
        },
        {
            label: 'New Step',
            icon: StepFilledIcon,
            onClick: (): void => handleNewClick('step'),
        },
    ];

    const handleRowClick = (data: any): void => {
        if (selectedTab === PIPELINES) {
            navigate(
                PIPELINES_AND_STEPS_PATHS.PIPELINE_DETAILS.replace(
                    ':id',
                    data.row.id
                )
            );
        }
        if (selectedTab === STEPS) {
            if (data.row.type === FIELD_MAPPING) {
                navigate(
                    `${PIPELINES_AND_STEPS_PATHS.UPDATE_STEP_DETAILS.replace(':id', data.row.id)}?option=${FIELD_MAPPING}`
                );
            } else if (data.row.type === RULE_SET) {
                navigate(
                    `${PIPELINES_AND_STEPS_PATHS.UPDATE_STEP_DETAILS.replace(':id', data.row.id)}?option=${RULE_SET}`
                );
            } else if (data.row.type === JAVASCRIPT_TRANSFORM) {
                navigate(
                    `${PIPELINES_AND_STEPS_PATHS.UPDATE_STEP_DETAILS.replace(':id', data.row.id)}?option=${JAVASCRIPT_TRANSFORM}`
                );
            }
        }
    };

    const handleDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const getEmptyStateHeading = (): string => {
        if (isDataPipelineServiceEnabled) {
            return 'Nothing to see here.';
        } else {
            return 'Upgrade Your Plan to Access this Feature';
        }
    };

    const getEmptyBodyMessage = (): React.ReactNode => {
        if (isDataPipelineServiceEnabled) {
            return `Add a new ${selectedTab === PIPELINES ? 'pipeline' : 'step'} using the button at the top right of the screen.`;
        } else {
            return (
                <Box sx={pipelinesAndStepsStyles.emptyStateBodyMessageStyles}>
                    <Typography>
                        With pipelines, you can automate the way your data
                        dictionary is built and maintained, making it easier to
                        keep everything consistent, accurate, and up to date.
                    </Typography>
                    <Typography fontWeight={500}>
                        This feature is currently disabled.Please contact your
                        admin or our sales team at sales @burstiq.com to enable
                        it.
                    </Typography>
                </Box>
            );
        }
    };

    return (
        <>
            <LandingPageWrapper
                title={'Pipelines'}
                showAddButton={isDataPipelineServiceEnabled}
                showMenuButton={isDataPipelineServiceEnabled}
                openMenuItems={!readOnly}
                menuOptions={dropdownMenuList}
                menuStyles={pipelinesAndStepsStyles.menuStyles}
                handleMenuButtonClick={handleMenuButtonClick}
                tableData={rowsData}
                isLoading={isLoading || pipelinesLoading || stepsLoading}
                columns={columns}
                getRowId={(row: any): string => row.id}
                onRowClick={handleRowClick}
                paginationPageSizeOptions={[25, 50, 100]}
                paginationPageSize={25}
                emptyStateIcon={
                    selectedTab === PIPELINES
                        ? EmptyPipelineIcon
                        : EmptyStepIcon
                }
                emptyStateHeading={getEmptyStateHeading()}
                emptyStateBodyMessage={getEmptyBodyMessage()}
                tabItems={isDataPipelineServiceEnabled ? TabItems : []}
                onTabChange={onTabChange}
                selectedTab={selectedTab}
                tabsStyles={pipelinesAndStepsStyles.tabsStyles}
                tabItemStyles={pipelinesAndStepsStyles.tabItemStyles}
                tableHeight={tableHeight}
                rowHeight={107}
                autoRowHeight={true}
                sortModel={[{ field: 'name', sort: 'asc' }]}
                {...(!isDataPipelineServiceEnabled && {
                    customImageStyles: pipelinesAndStepsStyles.emptyImageStyles,
                    customHeadingContainerStyles:
                        pipelinesAndStepsStyles.emptyHeadingContainerStyles,
                })}
            />
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handleDialogClose}
                />
            )}
        </>
    );
};
